import React, { useState, useEffect } from 'react';
import { Paper, Title, Button, Group, Text, Accordion, Textarea, Alert, Stack } from '@mantine/core';
import { PrimaryButton, ConsentCheckbox } from '../../components/UI';
import { IconAlertCircle } from '@tabler/icons-react';
import { isMinor } from '../../services/registrationService';

/**
 * Step 2: Consent Form
 * Collects all necessary consents and agreements
 */
const Step2Consent = ({
  dancerInfo,
  consents,
  saveConsents,
  saving,
  goToNextStep,
  goToPreviousStep,
  goToStep,
  isComplete = false,
  readOnly = false
}) => {
  const [formData, setFormData] = useState({
    competition_rules_agreed: false,
    media_release_agreed: false,
    media_promotional_agreed: false,
    liability_waiver_agreed: false,
    code_of_conduct_agreed: false,
    is_minor: false,
    parent_guardian_name: '',
    parent_guardian_signature: ''
  });

  const [errors, setErrors] = useState({});
  const [isUnder18, setIsUnder18] = useState(false);

  // Style for left-aligned labels
  const inputStyles = {
    label: { textAlign: 'left' },
    description: { textAlign: 'left' }
  };

  /**
   * Load existing consents and check if minor
   */
  useEffect(() => {
    // Check if dancer is a minor
    if (dancerInfo?.date_of_birth) {
      const minor = isMinor(dancerInfo.date_of_birth);
      setIsUnder18(minor);
      setFormData(prev => ({ ...prev, is_minor: minor }));
    }

    // Load existing consents
    if (consents) {
      setFormData({
        competition_rules_agreed: consents.competition_rules_agreed || false,
        media_release_agreed: consents.media_release_agreed || false,
        media_promotional_agreed: consents.media_promotional_agreed || false,
        liability_waiver_agreed: consents.liability_waiver_agreed || false,
        code_of_conduct_agreed: consents.code_of_conduct_agreed || false,
        is_minor: consents.is_minor || false,
        parent_guardian_name: consents.parent_guardian_name || '',
        parent_guardian_signature: consents.parent_guardian_signature || ''
      });
    }
  }, [dancerInfo, consents]);

  /**
   * Handle checkbox change
   */
  const handleCheckboxChange = (field, checked) => {
    setFormData(prev => ({ ...prev, [field]: checked }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  /**
   * Handle text input change
   */
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  /**
   * Validate form
   */
  const validate = () => {
    const newErrors = {};

    // All consents must be checked
    if (!formData.competition_rules_agreed) {
      newErrors.competition_rules_agreed = 'You must agree to the competition rules';
    }
    if (!formData.media_release_agreed) {
      newErrors.media_release_agreed = 'You must agree to media release';
    }
    if (!formData.media_promotional_agreed) {
      newErrors.media_promotional_agreed = 'You must agree to promotional use';
    }
    if (!formData.liability_waiver_agreed) {
      newErrors.liability_waiver_agreed = 'You must agree to the liability waiver';
    }
    if (!formData.code_of_conduct_agreed) {
      newErrors.code_of_conduct_agreed = 'You must agree to the code of conduct';
    }

    // Parent/Guardian signature if minor
    if (isUnder18) {
      if (!formData.parent_guardian_name.trim()) {
        newErrors.parent_guardian_name = 'Parent/Guardian name is required';
      }
      if (!formData.parent_guardian_signature.trim()) {
        newErrors.parent_guardian_signature = 'Parent/Guardian signature is required';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // Save consents
    const success = await saveConsents({
      ...formData,
      consent_ip_address: null // Could be captured if needed
    });

    if (success) {
      goToNextStep();
    }
  };

  /**
   * Check if all consents are agreed
   */
  const allConsentsAgreed = formData.competition_rules_agreed &&
    formData.media_release_agreed &&
    formData.media_promotional_agreed &&
    formData.liability_waiver_agreed &&
    formData.code_of_conduct_agreed;

  return (
    <form onSubmit={handleSubmit}>
      <fieldset disabled={readOnly} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
      <Paper
        radius="md"
        p={{ base: 'sm', sm: 'xl' }}
        withBorder
        style={{
          borderTop: '4px solid var(--primary-gold)',
          marginBottom: '2rem'
        }}
      >
        <Title order={2} size="h3" style={{ marginBottom: '1rem', color: 'var(--charcoal)' }}>
          Consent & Agreements
        </Title>

        <Text size="sm" color="dimmed" mb="xl" style={{ textAlign: 'left' }}>
          Please read and agree to all consents below to continue with your registration.
        </Text>

        {Object.keys(errors).length > 0 && (
          <Alert icon={<IconAlertCircle size="1rem" />} color="red" mb="xl">
            Please review and agree to all required consents
          </Alert>
        )}

        {/* Consent Sections */}
        <Accordion variant="separated" mb="xl">
          {/* Competition Rules */}
          <Accordion.Item value="rules">
            <Accordion.Control>Competition Rules & Regulations</Accordion.Control>
            <Accordion.Panel>
              <Text size="sm" mb="md" style={{ textAlign: 'left' }}>
                By registering for Prix du Canada, you agree to abide by all competition rules and regulations, including:
              </Text>
              <ul style={{ fontSize: '0.9rem', color: 'var(--soft-gray)', paddingLeft: '1.25rem', textAlign: 'left' }}>
                <li>Adherence to all technical requirements for variations</li>
                <li>Compliance with costume and music guidelines</li>
                <li>Respect for competition schedule and timing</li>
                <li>Acceptance of judges' decisions as final</li>
                <li>Professional behavior throughout the event</li>
              </ul>
            </Accordion.Panel>
          </Accordion.Item>

          {/* Media Release */}
          <Accordion.Item value="media">
            <Accordion.Control>Media Release & Professional Video Package</Accordion.Control>
            <Accordion.Panel>
              <Alert color="green" mb="md" styles={{ root: { backgroundColor: '#e8f5e9' } }}>
                <Text size="sm" weight={600} mb="xs" style={{ textAlign: 'left', color: '#2e7d32' }}>
                  🎁 Included: Professional Video ($100 value)
                </Text>
                <Text size="sm" style={{ textAlign: 'left' }}>
                  Every dancer gets a professional video of their performance, free!
                </Text>
              </Alert>
              <Text size="sm" mb="md" style={{ textAlign: 'left' }}>
                Prix du Canada will photograph and videograph the competition for archival, promotional, and participant video package purposes. By agreeing to this consent:
              </Text>
              <ul style={{ fontSize: '0.9rem', color: 'var(--soft-gray)', paddingLeft: '1.25rem', textAlign: 'left' }}>
                <li>You consent to being photographed and videographed during the competition</li>
                <li>You understand that media may be used on our website, social media, and promotional materials</li>
                <li>You agree to receive your professional performance video (included free)</li>
                <li>You waive any rights to compensation for such use</li>
                <li>You may request removal of specific promotional media by contacting us (performance video excluded)</li>
              </ul>
            </Accordion.Panel>
          </Accordion.Item>

          {/* Liability Waiver */}
          <Accordion.Item value="liability">
            <Accordion.Control>Liability Waiver</Accordion.Control>
            <Accordion.Panel>
              <Text size="sm" mb="md" style={{ textAlign: 'left' }}>
                Dance and physical activity carry inherent risks. By participating in Prix du Canada:
              </Text>
              <ul style={{ fontSize: '0.9rem', color: 'var(--soft-gray)', paddingLeft: '1.25rem', textAlign: 'left' }}>
                <li>You acknowledge the risks associated with dance performance</li>
                <li>You accept full responsibility for any injury or loss</li>
                <li>You waive liability claims against Prix du Canada organization, staff, and venue</li>
                <li>You confirm you are physically capable of participation</li>
                <li>You have adequate health insurance coverage</li>
              </ul>
            </Accordion.Panel>
          </Accordion.Item>

          {/* Code of Conduct */}
          <Accordion.Item value="conduct">
            <Accordion.Control>Code of Conduct</Accordion.Control>
            <Accordion.Panel>
              <Text size="sm" mb="md" style={{ textAlign: 'left' }}>
                All participants must maintain professional conduct. This includes:
              </Text>
              <ul style={{ fontSize: '0.9rem', color: 'var(--soft-gray)', paddingLeft: '1.25rem', textAlign: 'left' }}>
                <li>Respectful behavior toward judges, staff, and other participants</li>
                <li>No harassment, bullying, or discriminatory behavior</li>
                <li>Compliance with venue rules and regulations</li>
                <li>Appropriate attire and presentation at all times</li>
                <li>Understanding that violations may result in disqualification</li>
              </ul>
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>

        {/* Consent Checkboxes */}
        <Stack gap="lg" mb="xl">
          <ConsentCheckbox
            label="I have read and agree to abide by the Prix du Canada competition rules and regulations"
            checked={formData.competition_rules_agreed}
            onChange={(e) => handleCheckboxChange('competition_rules_agreed', e.target.checked)}
            error={errors.competition_rules_agreed}
            disabled={isComplete}
          />

          <ConsentCheckbox
            label="I consent to photography and videography during the competition and agree to receive my complimentary professional performance video (Required for free video package)"
            checked={formData.media_release_agreed}
            onChange={(e) => handleCheckboxChange('media_release_agreed', e.target.checked)}
            error={errors.media_release_agreed}
            disabled={isComplete}
            bold
          />

          <ConsentCheckbox
            label="I consent to Prix du Canada using my images for promotional purposes (Required)"
            checked={formData.media_promotional_agreed}
            onChange={(e) => handleCheckboxChange('media_promotional_agreed', e.target.checked)}
            error={errors.media_promotional_agreed}
            disabled={isComplete}
          />

          <ConsentCheckbox
            label="I acknowledge the risks and waive liability for Prix du Canada organization"
            checked={formData.liability_waiver_agreed}
            onChange={(e) => handleCheckboxChange('liability_waiver_agreed', e.target.checked)}
            error={errors.liability_waiver_agreed}
            disabled={isComplete}
          />

          <ConsentCheckbox
            label="I agree to maintain professional conduct throughout the competition"
            checked={formData.code_of_conduct_agreed}
            onChange={(e) => handleCheckboxChange('code_of_conduct_agreed', e.target.checked)}
            error={errors.code_of_conduct_agreed}
            disabled={isComplete}
          />
        </Stack>

        {/* Parent/Guardian Signature (if minor) */}
        {isUnder18 && (
          <>
            <Alert color="blue" mb="xl">
              <Text weight={600} mb="xs">Parent/Guardian Consent Required</Text>
              <Text size="sm">
                As the dancer is under 18 years of age, a parent or legal guardian must provide consent on their behalf.
              </Text>
            </Alert>

            <Stack spacing="md">
              <Textarea
                label="Parent/Guardian Full Name"
                placeholder="Enter your full name"
                required
                value={formData.parent_guardian_name}
                onChange={(e) => handleInputChange('parent_guardian_name', e.target.value)}
                error={errors.parent_guardian_name}
                description="I am the parent/legal guardian and provide consent on behalf of the minor"
                styles={inputStyles}
              />

              <Textarea
                label="Electronic Signature"
                placeholder="Type your full name to sign electronically"
                required
                value={formData.parent_guardian_signature}
                onChange={(e) => handleInputChange('parent_guardian_signature', e.target.value)}
                error={errors.parent_guardian_signature}
                description="By typing your name, you are providing an electronic signature"
                styles={inputStyles}
              />

              <Text size="sm" color="dimmed">
                Date: {new Date().toLocaleDateString()}
              </Text>
            </Stack>
          </>
        )}

        {/* Summary */}
        {allConsentsAgreed && (
          <Alert color="green" mt="xl">
            <Text weight={600}>All consents provided</Text>
            <Text size="sm">You may now proceed to the next step.</Text>
          </Alert>
        )}
      </Paper>
      </fieldset>

      {/* Navigation Buttons */}
      <Group justify="space-between" mt="xl">
        <Button variant="subtle" onClick={goToPreviousStep} disabled={saving}>
          Back
        </Button>

        {readOnly ? (
          <PrimaryButton type="button" onClick={() => goToStep && goToStep(3)}>
            Next: Variations →
          </PrimaryButton>
        ) : (
          <PrimaryButton type="submit" loading={saving} disabled={!allConsentsAgreed}>
            I Agree & Continue
          </PrimaryButton>
        )}
      </Group>
    </form>
  );
};

export default Step2Consent;
